import React, { useState, useEffect } from 'react';
import { blogApi, type BlogPost } from '../services/api';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { Import, Loader2, ExternalLink, Clock, Trash2, BookOpen } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/ui/use-toast';
import { motion, AnimatePresence } from 'framer-motion';

type ImportSource = 'medium' | 'linkedin';

export function BlogImportPage() {
  const [url, setUrl] = useState('');
  const [source, setSource] = useState<ImportSource>('medium');
  const [loading, setLoading] = useState(false);
  const [importedPosts, setImportedPosts] = useState<BlogPost[]>([]);
  const { isAdmin } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      const posts = await blogApi.getAll();
      setImportedPosts(posts);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load existing posts",
        variant: "destructive",
      });
    }
  };

  const handleImport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url) return;

    setLoading(true);
    try {
      let post: BlogPost;
      
      if (source === 'medium') {
        post = await blogApi.importFromMedium(url);
      } else {
        post = await blogApi.importFromLinkedIn(url);
      }

      setImportedPosts(prev => [post, ...prev]);
      toast({
        title: "Success",
        description: `${source === 'medium' ? 'Medium' : 'LinkedIn'} post imported successfully`,
      });
      setUrl('');
    } catch (error) {
      toast({
        title: "Import Failed",
        description: error instanceof Error ? error.message : "Failed to import post",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const deletePost = async (postId: string) => {
    try {
      setImportedPosts(posts => posts.filter(p => p.id !== postId));
      toast({
        title: "Success",
        description: "Post deleted successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete post",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs 
          items={[
            { label: 'Content', href: '/content' },
            { label: 'Blog Import' }
          ]} 
        />

        <div className="mt-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-6">Import Blog Posts</h1>
            
            {isAdmin && (
              <form onSubmit={handleImport} className="mb-8 space-y-4">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Import Source
                    </label>
                    <div className="flex space-x-4">
                      <button
                        type="button"
                        onClick={() => setSource('medium')}
                        className={`px-4 py-2 rounded-md flex items-center space-x-2 ${
                          source === 'medium'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <span>Medium</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setSource('linkedin')}
                        className={`px-4 py-2 rounded-md flex items-center space-x-2 ${
                          source === 'linkedin'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <span>LinkedIn</span>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-1">
                    <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-2">
                      {source === 'medium' ? 'Medium Article URL' : 'LinkedIn Article URL'}
                    </label>
                    <input
                      type="url"
                      id="url"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      placeholder={`https://${source}.com/...`}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="self-end px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center space-x-2"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Importing...</span>
                      </>
                    ) : (
                      <>
                        <Import className="w-5 h-5" />
                        <span>Import</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}

            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">Imported Posts</h2>
              <AnimatePresence>
                {importedPosts.map((post) => (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="bg-gray-50 rounded-lg p-6 border border-gray-200"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h3 className="text-lg font-medium text-gray-900">{post.title}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            post.source === 'medium' 
                              ? 'bg-green-100 text-green-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {post.source}
                          </span>
                        </div>
                        
                        <p className="mt-1 text-sm text-gray-500">
                          By {post.author} • {new Date(post.publishedAt).toLocaleDateString()}
                        </p>
                        
                        {post.preview && (
                          <p className="mt-2 text-gray-600">{post.preview}</p>
                        )}

                        <div className="mt-4 flex items-center space-x-6">
                          <a
                            href={post.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm flex items-center space-x-1"
                          >
                            <ExternalLink className="w-4 h-4" />
                            <span>View Original</span>
                          </a>
                          
                          <div className="flex items-center space-x-1 text-gray-500">
                            <Clock className="w-4 h-4" />
                            <span className="text-sm">{post.estimatedReadTime} min read</span>
                          </div>

                          <div className="flex items-center space-x-2">
                            {post.tags.map((tag) => (
                              <span
                                key={tag}
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => window.open(`/content/blog/${post.id}`, '_blank')}
                          className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md"
                          title="Read Post"
                        >
                          <BookOpen className="w-5 h-5" />
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => deletePost(post.id)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md"
                            title="Delete Post"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {importedPosts.length === 0 && (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <Import className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No posts imported yet</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {isAdmin 
                      ? 'Import your first article to get started'
                      : 'No blog posts have been imported yet'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}